import { getAccessIssuesForAttachments } from "owa-attachment-policy-access-issue-checker";
import type { AttachmentWellViewState } from "owa-attachment-well-data";
import { lazyGetAttachmentWellInitialValue, lazyInitializeAttachments, lazyReinitializeAttachments, } from "owa-attachment-well-data";
import type { ClientItem } from "owa-mail-store";
import getSmimeAttachmentType from "owa-smime/lib/utils/getSmimeAttachmentType";
import isSmimeDecoded from "owa-smime/lib/utils/isSmimeDecoded";
import isSMIMEItem from "owa-smime/lib/utils/isSMIMEItem";
import { mutatorAction } from "satcheljs";
import type ItemViewState from "../store/schema/ItemViewState";
import registerGetAttachmentWellViewStateByItemHelper from "../utils/registerGetAttachmentWellViewStateByItemHelper";
import addInfoBarMessage from "owa-info-bar/lib/actions/addInfoBarMessage";
import type Message from "owa-service/lib/contract/Message";
import doesFolderIdEqualName from "owa-session-store/lib/utils/doesFolderIdEqualName";
import type { ClientItemId } from "owa-client-ids";

export default async function initializeAttachmentsForItem(itemViewState: ItemViewState, item: ClientItem | undefined, reinitialize: boolean = false): Promise<void> {
    if (!item ||
        (isSMIMEItem(item) && !showSMIMEAttachments(itemViewState, item)) ||
        !item.Attachments) {
        return;
    }
    const message = item as Message;
    const isJunkItem = !!message?.ParentFolderId?.Id &&
        doesFolderIdEqualName(message.ParentFolderId.Id, "junkemail");
    
    if (!itemViewState.attachmentWell) {
        const getAttachmentWellInitialValue = await lazyGetAttachmentWellInitialValue.import();
        setAttachmentWellForItem(itemViewState, getAttachmentWellInitialValue(true /*isReadOnly*/, false /*isInitialized*/, "" /*composeId is empty in read only mode*/));
    }
    
    registerGetAttachmentWellViewStateByItemHelper();
    
    const clientItemId: ClientItemId | undefined = item.ItemId
        ? {
            Id: item.ItemId.Id,
            mailboxInfo: item.MailboxInfo,
        }
        : undefined;
    
    const itemBody: string | undefined = item.NormalizedBody?.BodyType === "HTML" ? item.NormalizedBody?.Value : undefined;
    
    if (reinitialize) {
        await lazyReinitializeAttachments.importAndExecute(item.Attachments, item.MailboxInfo, itemViewState.attachmentWell, true /* forceUpdateAttachmentsInStore */, undefined /* unsupportedMenuActions */, clientItemId /*parentItemId*/, !!item.RightsManagementLicenseData /* isMessageEncrypted */, isSMIMEItem(item), isProtectedUMVoiceMessageItemClass(item.ItemClass), isJunkItem, false /* isCalendarItem */, itemBody);
    }
    else {
        await lazyInitializeAttachments.importAndExecute(item.Attachments, item.MailboxInfo, itemViewState.attachmentWell, true /* forceUpdateAttachmentsInStore */, false /* isInitializationForCompose */, undefined /* unsupportedMenuActions */, clientItemId /*parentItemId*/, !!item.RightsManagementLicenseData /* isMessageEncrypted */, isSMIMEItem(item), isProtectedUMVoiceMessageItemClass(item.ItemClass), isJunkItem, false /* isCalendarItem */, itemBody);
    }
    
    const infoBarIds = getAccessIssuesForAttachments(item.Attachments, item.MailboxInfo);
    addInfoBarMessage(itemViewState, infoBarIds);
    
    const hasAttachments = (itemViewState.attachmentWell?.docViewAttachments &&
        itemViewState.attachmentWell?.docViewAttachments.length > 0) ||
        (itemViewState.attachmentWell?.imageViewAttachments &&
            itemViewState.attachmentWell?.imageViewAttachments.length > 0);
    if (isJunkItem && hasAttachments) {
        addInfoBarMessage(itemViewState, "infoBlockJunkAttachments");
    }
}

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
const setAttachmentWellForItem = mutatorAction("setAttachmentWellForItem", (itemViewState: ItemViewState, viewState: AttachmentWellViewState) => {
    itemViewState.attachmentWell = viewState;
});

function showSMIMEAttachments(itemViewState: ItemViewState, item: ClientItem): boolean {
    // Do not display attachments for undecoded S/MIME items or decoded S/MIME items in conversation view.
    return !((!!getSmimeAttachmentType(item) && !isSmimeDecoded(item)) ||
        (isSmimeDecoded(item) && itemViewState.isConversationItemPart));
}

function isProtectedUMVoiceMessageItemClass(itemClass: string | undefined) {
    if (!itemClass) {
        return false;
    }
    switch (itemClass) {
        case "IPM.Note.rpmsg.Microsoft.Voicemail.UM.CA":
        case "IPM.Note.rpmsg.Microsoft.Voicemail.UM":
            return true;
        default:
            return false;
    }
}
